import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    Box
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import FormInput from '../../inputs/FormInput';
import LanguageToggle from '../../global/LanguageToggle';
import InputFileUpload from '../../inputs/InputFileUpload';

const FormAddEditOptionValue = ({
    open,
    onClose,
    currentData,
    isEdit = false,
    onSubmit,
    loading = false,
    displayType = 'dropdown'
}) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || 'en';
    const isArabic = lng.startsWith('ar');
    const [activeLang, setActiveLang] = useState(isArabic ? 'ar' : 'en');

    // Form States
    const [nameAr, setNameAr] = useState('');
    const [nameEn, setNameEn] = useState('');
    const [value, setValue] = useState('');
    const [isActive, setIsActive] = useState(true);

    useEffect(() => {
        if (open) {
            if (isEdit && currentData) {
                setNameAr(currentData.name?.ar || '');
                setNameEn(currentData.name?.en || '');
                // value could be an image object {id, url, ...} or a string/hex
                setValue(currentData.image || currentData.value || '');
                setIsActive(currentData.is_active ?? true);
            } else {
                // Reset form for new entry
                setNameAr('');
                setNameEn('');
                setValue('');
                setIsActive(true);
            }
        }
    }, [open, isEdit, currentData]);

    const handleFormSubmit = (e) => {
        e.preventDefault();

        const trimmedEn = nameEn.trim();
        const trimmedAr = nameAr.trim();

        const payload = {
            name: {
                ar: trimmedAr || trimmedEn,
                en: trimmedEn
            },
            is_active: isActive,
            sort_order: isEdit ? currentData.sort_order : 0
        };

        if (displayType === 'image') {
            if (typeof value === 'string') {
                // It's a temp filename from auto-upload
                payload.image = value;
            } else if (value === null) {
                // Explicitly removed
                payload.remove_image = true;
            }
            // If value is an object (existing image), we don't need to send it back unless it changed
        } else {
            payload.value = value;
        }

        onSubmit(payload);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 2 }
            }}
        >
            <DialogTitle sx={{ fontWeight: 'bold', borderBottom: '1px solid', borderColor: 'divider', mb: 2 }}>
                {isEdit
                    ? (isArabic ? 'تعديل قيمة' : 'Edit Value')
                    : (isArabic ? 'إضافة قيمة جديدة' : 'Add New Value')}
            </DialogTitle>

            <Box component="form" onSubmit={handleFormSubmit}>
                <DialogContent>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                        <LanguageToggle activeLang={activeLang} onChange={setActiveLang} showLabel={true} />
                    </Box>

                    <Grid container spacing={2}>
                        {/* Name (AR) */}
                        {activeLang === 'ar' && (
                            <Grid item xs={12}>
                                <FormInput
                                    label="الاسم (عربي)"
                                    placeholder="مثلاً: أحمر، كبير، 128 جيجا"
                                    value={nameAr}
                                    onChange={(e) => setNameAr(e.target.value)}
                                    helperText="اختياري — لو سيبتها فاضية هتتعبّى تلقائياً من الاسم الإنجليزي"
                                />
                            </Grid>
                        )}

                        {/* Name (EN) */}
                        {activeLang === 'en' && (
                            <Grid item xs={12}>
                                <FormInput
                                    label="Name (English)"
                                    placeholder="e.g., Red, Large, 128GB"
                                    value={nameEn}
                                    onChange={(e) => setNameEn(e.target.value)}
                                    required
                                />
                            </Grid>
                        )}

                        {/* Value Field - Specific handling for Color Picker and Image Upload */}
                        <Grid item xs={12}>
                            {displayType === 'color' ? (
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                                    <Box sx={{ flexGrow: 1 }}>
                                        <FormInput
                                            label={isArabic ? 'كود اللون (HEX)' : 'Color Code (HEX)'}
                                            placeholder="#FFFFFF"
                                            value={value}
                                            onChange={(e) => setValue(e.target.value)}
                                            required
                                        />
                                    </Box>
                                    <input
                                        type="color"
                                        value={value.startsWith('#') ? value : '#000000'}
                                        onChange={(e) => setValue(e.target.value.toUpperCase())}
                                        style={{
                                            width: '50px',
                                            height: '45px',
                                            cursor: 'pointer',
                                            border: '1px solid #ccc',
                                            borderRadius: '4px',
                                            marginBottom: '4px'
                                        }}
                                    />
                                </Box>
                            ) : displayType === 'image' ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
                                    <InputFileUpload
                                        labelImage={isArabic ? "صورة القيمة" : "Value Image"}
                                        value={value}
                                        onChange={(e) => {
                                            // value now comes from InputFileUpload target.value (string or null)
                                            setValue(e.target.value);
                                        }}
                                        width={150}
                                        height={150}
                                        borderRadius="8px"
                                        required={!isEdit && !value}
                                    />
                                </Box>
                            ) : (
                                <FormInput
                                    label={isArabic ? 'القيمة' : 'Value'}
                                    placeholder={isArabic ? 'القيمة التقنية (مثل: red, L, 128gb)' : 'Technical value (e.g., red, L, 128gb)'}
                                    value={value}
                                    onChange={(e) => setValue(e.target.value)}
                                    required
                                />
                            )}
                        </Grid>

                        {/* Active Status */}
                        <Grid item xs={12}>
                            <FormInput
                                label={isArabic ? 'نشط' : 'Active'}
                                type="switch"
                                checked={isActive}
                                onChange={(e) => setIsActive(e.target.checked)}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions sx={{ p: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Button onClick={onClose} variant="outlined" color="inherit" disabled={loading}>
                        {isArabic ? 'إلغاء' : 'Cancel'}
                    </Button>
                    <Button type="submit" variant="contained" color="primary" disabled={loading}>
                        {loading ? (isArabic ? 'جارٍ الحفظ...' : 'Saving...') : (isArabic ? 'حفظ' : 'Save')}
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    );
};

export default FormAddEditOptionValue;
